import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('ko-KR', {
    style: 'currency',
    currency: 'KRW',
  }).format(price)
}

export const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

export const formatDateTime = (date: Date): string => {
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

// 한국어 숫자 포맷팅
export function formatKoreanNumber(num: number): string {
  if (num >= 10000) {
    return (num / 10000).toFixed(1).replace(/\.0$/, '') + '만';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + '천';
  }
  return num.toString();
}

// 한국어 가격 포맷팅 
export function formatKoreanPrice(price: number): string {
  return `${formatKoreanNumber(price)}원`;
}

// 상대적 시간 표시 (예: 2시간 전)
export function getRelativeTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}일 전`;
  } else if (hours > 0) {
    return `${hours}시간 전`;
  } else if (minutes > 0) {
    return `${minutes}분 전`;
  } else {
    return '방금 전';
  }
}

// 거리 포맷팅
export function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)}km`;
  }
  return `${Math.round(meters)}m`;
}

// 할인율 계산
export function calculateDiscountRate(originalPrice: number, discountedPrice: number): number {
  return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
}

// 이미지 URL 생성 (Supabase Storage)
export function getImageUrl(bucket: string, path: string): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return `${supabaseUrl}/storage/v1/object/public/${bucket}/${path}`;
}

// 썸네일 이미지 URL 생성
export function getThumbnailUrl(bucket: string, path: string): string {
  const pathParts = path.split('/');
  const filename = pathParts.pop();
  const directory = pathParts.join('/');
  
  return getImageUrl(bucket, `${directory}/thumb/${filename?.replace(/\.(jpg|jpeg|png)$/i, '.webp')}`);
}

// 디바이스 타입 감지
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  
  if (width < 768) {
    return 'mobile';
  } else if (width < 1024) {
    return 'tablet';
  } else {
    return 'desktop';
  }
}

// 디바운스 함수
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

// 쓰로틀 함수
export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

export function cleanAddressForGeocoding(address: string): string {
  if (!address) return '';

  // 1. 괄호와 그 안의 내용 먼저 제거 (e.g., "(삼성빌딩)")
  let cleaned = address.replace(/\s*\([^)]*\)/g, '');

  // 2. '로', '길'로 끝나는 도로명과 그 뒤의 건물번호(숫자)까지만 추출하는 정규식
  //    "백범로 80 1층" -> "백범로 80"
  //    "테헤란로 22, 301호" -> "테헤란로 22"
  const addressRegex = /.*(로|길)\s*\d+(-*\d*|\s*)/;
  const match = cleaned.match(addressRegex);

  // 정규식에 매칭되는 부분이 있으면 그 부분만 사용, 없으면 원본(괄호제거) 사용
  if (match) {
    cleaned = match[0].trim();
  } else {
    // 혹시 모를 다른 주소 형식을 위해 쉼표 기준으로 한 번 더 처리
    cleaned = cleaned.split(',')[0].trim();
  }

  return cleaned;
}